import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { jsPDF } from 'jspdf';
import '../styles/DetailedSaleForm.css';

const SellerDetailedSaleForm = ({ onSaleAdded, onCancel }) => {
  // eslint-disable-next-line no-unused-vars
  const { user } = useAuth();
  const isMounted = useRef(true);
  const [formData, setFormData] = useState({
    sale_date: new Date().toISOString().split('T')[0],
    payment_method: 'Efectivo',
    customer_id: '',
    notes: ''
  });
  
  // Estados para datos
  const [assignedPointOfSale, setAssignedPointOfSale] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [businessInfo, setBusinessInfo] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [customers, setCustomers] = useState([]);
  
  // Estados para selección de productos
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [productQuantity, setProductQuantity] = useState('');
  const [productDiscount, setProductDiscount] = useState(0.00);
  
  // Estados para cálculos
  const [generalDiscount, setGeneralDiscount] = useState(0.00);
  const [taxRate, setTaxRate] = useState(0.00);
  const [subtotal, setSubtotal] = useState(0.00);
  const [totalBeforeTax, setTotalBeforeTax] = useState(0.00);
  const [grandTotal, setGrandTotal] = useState(0.00);
  
  // Estados para UI
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [businessDateTime, setBusinessDateTime] = useState(null);
  const [printTicket, setPrintTicket] = useState(false);
  const [isGeneratingTicket, setIsGeneratingTicket] = useState(false);

  // Función para obtener la fecha y hora del negocio desde el servidor
  const fetchBusinessTime = async (businessId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No se encontró token de autenticación');
        return null;
      }

      const response = await axios.get(`/api/sales/server-time?business_id=${businessId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        timeout: 5000 // Timeout de 5 segundos para evitar bloqueos
      });

      console.log('Respuesta completa del servidor:', response.data);

      if (response.data && response.data.time) {
        console.log('Fecha y hora del negocio obtenida:', response.data.time);
        setBusinessDateTime(response.data.time);
        return response.data.time;
      } else {
        console.warn('La respuesta del servidor no contiene fecha/hora');
        return null;
      }
    } catch (error) {
      console.error('Error al obtener fecha/hora del negocio:', error);
      return null;
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    // Establecer la referencia de montaje
    isMounted.current = true;
    console.log('SellerDetailedSaleForm - Iniciando carga de datos');
    
    const fetchInitialData = async () => {
      try {
        console.log('SellerDetailedSaleForm - Verificando token');
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('SellerDetailedSaleForm - No se encontró token');
          if (isMounted.current) {
            setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
            setInitialLoading(false);
          }
          return;
        }

        // Obtener el punto de venta asignado
        const response = await axios.get('/api/points-of-sale/assigned', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (isMounted.current) {
          // Continuar con el flujo normal
        }

        // Verificar si hay puntos de venta asignados
        if (response.data && response.data.pointsOfSale && response.data.pointsOfSale.length > 0) {
          const pointOfSale = response.data.pointsOfSale[0];
          console.log('SellerDetailedSaleForm - Punto de venta encontrado:', pointOfSale);
          
          if (isMounted.current) {
            setAssignedPointOfSale(pointOfSale);
          }
          
          // Guardar información del negocio si está disponible
          if (response.data.businessInfo) {
            console.log('SellerDetailedSaleForm - Información del negocio:', response.data.businessInfo);
            if (isMounted.current) {
              setBusinessInfo(response.data.businessInfo);
            }
          }
          
          // Obtener el ID del negocio
          const businessId = pointOfSale.business_id;
          console.log('SellerDetailedSaleForm - ID del negocio:', businessId);
          
          if (!businessId) {
            console.error('SellerDetailedSaleForm - No se encontró business_id en el punto de venta');
            if (isMounted.current) {
              setError('Error: El punto de venta no tiene un negocio asociado.');
              setInitialLoading(false);
            }
            return;
          }
          
          // Cargar productos del negocio
          console.log(`SellerDetailedSaleForm - Cargando productos del negocio ${businessId}`);
          try {
            // Usar el endpoint correcto para obtener productos como vendedor
            const productsResponse = await axios.get('/api/products/business-products', {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            console.log('SellerDetailedSaleForm - Productos obtenidos:', productsResponse.data);
            
            // Productos obtenidos correctamente
            if (isMounted.current) {
              // Ordenar alfabéticamente por nombre
              const sortedProducts = [...(productsResponse.data || [])];
              sortedProducts.sort((a, b) => a.prod_name.localeCompare(b.prod_name));
              
              setProducts(sortedProducts);
              setFilteredProducts(sortedProducts);
            }
          } catch (productError) {
            console.error('SellerDetailedSaleForm - Error al cargar productos:', productError);
            if (isMounted.current) {
              setError(`Error al cargar productos: ${productError.message}`);
            }
          }
          
          // Cargar clientes del negocio
          console.log(`SellerDetailedSaleForm - Cargando clientes del negocio ${businessId}`);
          try {
            // Usar el endpoint correcto para obtener clientes como vendedor
            const customersResponse = await axios.get('/api/customers/seller', {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            console.log('SellerDetailedSaleForm - Clientes obtenidos:', customersResponse.data);
            
            // Clientes obtenidos correctamente
            if (isMounted.current) {
              const customersList = customersResponse.data || [];
              setCustomers(customersList);
              
              // Si hay clientes, seleccionar el cliente "Publico en General" por defecto si existe
              if (customersList.length > 0) {
                const publicCustomer = customersList.find(c => c.cus_name === 'Publico en General');
                if (publicCustomer) {
                  setFormData(prevState => ({
                    ...prevState,
                    customer_id: publicCustomer.id
                  }));
                  console.log('Cliente "Publico en General" seleccionado por defecto:', publicCustomer.id);
                } else {
                  // Si no existe "Publico en General", seleccionar el primer cliente
                  setFormData(prevState => ({
                    ...prevState,
                    customer_id: customersList[0].id
                  }));
                  console.log('Primer cliente seleccionado por defecto:', customersList[0].id);
                }
              }
            }
          } catch (customerError) {
            console.error('SellerDetailedSaleForm - Error al cargar clientes:', customerError);
            if (isMounted.current) {
              setError(`Error al cargar clientes: ${customerError.message}`);
            }
          }
        } else {
          console.error('SellerDetailedSaleForm - No se encontró punto de venta asignado');
          if (isMounted.current) {
            setError('No se encontró un punto de venta asignado para este vendedor. Por favor, contacte al administrador.');
          }
        }
      } catch (error) {
        console.error('SellerDetailedSaleForm - Error al cargar datos iniciales:', error);
        if (isMounted.current) {
          setError(`Error al cargar datos: ${error.message}. Por favor, inténtelo de nuevo.`);
        }
      } finally {
        console.log('SellerDetailedSaleForm - Finalizando carga inicial');
        if (isMounted.current) {
          setInitialLoading(false);
        }
      }
    };
    
    fetchInitialData();
    
    // Limpieza al desmontar
    return () => {
      console.log('SellerDetailedSaleForm - Componente desmontado');
      isMounted.current = false;
    };
  }, []);

  // Filtrar productos cuando cambia el término de búsqueda
  useEffect(() => {
    if (!isMounted.current) return;
    
    if (!productSearchTerm.trim()) {
      // Si no hay término de búsqueda, mostrar todos los productos
      setFilteredProducts(products);
    } else {
      // Filtrar productos por código o nombre
      const searchTermLower = productSearchTerm.toLowerCase().trim();
      const filtered = products.filter(product => 
        (product.prod_code && product.prod_code.toLowerCase().includes(searchTermLower)) || 
        product.prod_name.toLowerCase().includes(searchTermLower)
      );
      setFilteredProducts(filtered);
      
      // Si hay resultados filtrados, seleccionar el primer producto
      if (filtered.length > 0) {
        setSelectedProductId(filtered[0].id);
      }
    }
  }, [productSearchTerm, products]);

  // Calcular subtotal cuando cambian los productos seleccionados
  useEffect(() => {
    if (!isMounted.current) return;
    
    // Calcular subtotal (suma de todos los productos)
    const calculatedSubtotal = selectedProducts.reduce((acc, product) => {
      // Asegurar que discount sea un número para el cálculo
      const discountValue = typeof product.discount === 'string' ?
        (product.discount === '' || product.discount === '.' ? 0 : parseFloat(product.discount)) :
        product.discount;
      
      // Usar prod_price en lugar de price para acceder al precio del producto
      return acc + (product.prod_price * product.quantity - discountValue);
    }, 0);
    
    setSubtotal(calculatedSubtotal);
  }, [selectedProducts]);

  // Calcular total antes de impuesto y gran total
  useEffect(() => {
    // Asegurar que generalDiscount sea un número para el cálculo
    const discountValue = typeof generalDiscount === 'string' ?
      (generalDiscount === '' || generalDiscount === '.' ? 0 : parseFloat(generalDiscount)) :
      generalDiscount;
    
    // Calcular el total antes de impuestos (subtotal - descuento general)
    const afterDiscount = Math.max(0, subtotal - discountValue);
    setTotalBeforeTax(afterDiscount);
    
    // Calcular el impuesto y el gran total
    const taxAmount = afterDiscount * (taxRate / 100);
    setGrandTotal(afterDiscount + taxAmount);
  }, [subtotal, generalDiscount, taxRate]);

  // Manejadores de eventos
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      if (name === 'printTicket') {
        setPrintTicket(checked);
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleProductChange = (e) => {
    setSelectedProductId(e.target.value);
  };

  const handleProductSearchChange = (e) => {
    setProductSearchTerm(e.target.value);
  };

  const handleQuantityChange = (e) => {
    const inputValue = e.target.value;
    
    // Permitir campo vacío o valores decimales válidos
    if (inputValue === '' || inputValue === '.') {
      setProductQuantity(inputValue);
      return;
    }
    
    // Validar que sea un número y que no tenga más de 2 decimales
    if (/^\d*\.?\d{0,2}$/.test(inputValue)) {
      setProductQuantity(inputValue);
    }
  };

  const handleDiscountChange = (e) => {
    // Obtener el valor del input y permitir solo números y un solo punto decimal
    let value = e.target.value;
    
    // Permitir entrada vacía o valores parciales como "0." durante la edición
    if (value === '' || value === '.') {
      setProductDiscount(value); // Guardar el valor tal cual para permitir la edición
      return;
    }
    
    // Validar que solo contenga números y un punto decimal
    if (/^\d*\.?\d*$/.test(value)) {
      // Si es un valor válido, actualizar el estado
      setProductDiscount(value);
    }
  };

  const handleGeneralDiscountChange = (e) => {
    // Obtener el valor del input y permitir solo números y un solo punto decimal
    let value = e.target.value;
    
    // Permitir entrada vacía o valores parciales como "0." durante la edición
    if (value === '' || value === '.') {
      setGeneralDiscount(value); // Guardar el valor tal cual para permitir la edición
      return;
    }
    
    // Validar que solo contenga números y un punto decimal
    if (/^\d*\.?\d*$/.test(value)) {
      // Si es un valor válido, actualizar el estado
      setGeneralDiscount(value);
    }
  };

  const handleTaxRateChange = (e) => {
    const value = parseFloat(e.target.value);
    if (value >= 0) {
      setTaxRate(value);
    }
  };

  const handleAddProduct = () => {
    // Verificar que se haya seleccionado un producto
    if (!selectedProductId) {
      setError('Por favor, seleccione un producto.');
      return;
    }
    
    // Verificar que la cantidad sea válida
    if (productQuantity === '' || productQuantity === '.') {
      setError('Por favor, ingrese una cantidad válida.');
      return;
    }
    
    const quantityValue = parseFloat(productQuantity);
    if (isNaN(quantityValue) || quantityValue <= 0) {
      setError('La cantidad debe ser un número mayor a 0.');
      return;
    }
    
    // Buscar el producto seleccionado
    const product = products.find(p => p.id.toString() === selectedProductId.toString());
    if (!product) {
      setError('Producto no encontrado.');
      return;
    }
    
    // Convertir el descuento a número para almacenarlo
    const discountValue = productDiscount === '' || productDiscount === '.' ? 
      0 : parseFloat(productDiscount);
    
    // Verificar si el producto ya está en la lista
    const existingProduct = selectedProducts.find(
      p => p.id.toString() === selectedProductId.toString()
    );
    
    if (existingProduct) {
      // Si el producto ya está en la lista, actualizar la cantidad y el descuento
      setSelectedProducts(
        selectedProducts.map(p => 
          p.id.toString() === selectedProductId.toString()
            ? { 
                ...p, 
                quantity: parseFloat(p.quantity) + parseFloat(productQuantity),
                discount: (typeof existingProduct.discount === 'string' ? 
                  (existingProduct.discount === '' || existingProduct.discount === '.' ? 0 : parseFloat(existingProduct.discount)) : 
                  existingProduct.discount) + discountValue
              } 
            : p
        )
      );
    } else {
      // Si el producto no está en la lista, agregarlo
      setSelectedProducts([
        ...selectedProducts,
        {
          ...product,
          quantity: parseFloat(productQuantity),
          discount: discountValue
        }
      ]);
    }
    
    // Resetear la cantidad a vacío y el descuento a vacío para el próximo producto
    setSelectedProductId('');
    setProductQuantity('');
    setProductDiscount('');
    setError('');
  };

  const handleUpdateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
    } else {
      setSelectedProducts(
        selectedProducts.map(p => 
          p.id === productId 
            ? { ...p, quantity: parseFloat(newQuantity) } 
            : p
        )
      );
    }
  };

  const handleUpdateDiscount = (productId, newDiscount) => {
    // Si es un valor vacío o solo un punto, permitir la edición
    if (newDiscount === '' || newDiscount === '.') {
      setSelectedProducts(
        selectedProducts.map(p => 
          p.id === productId 
            ? { ...p, discount: newDiscount } 
            : p
        )
      );
      return;
    }
    
    // Convertir a número para validar
    const discountValue = parseFloat(newDiscount);
    if (isNaN(discountValue) || discountValue < 0) {
      return;
    }
    
    setSelectedProducts(
      selectedProducts.map(p => 
        p.id === productId 
          ? { ...p, discount: newDiscount } 
          : p
      )
    );
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(product => product.id !== productId));
  };

  // Función para generar el ticket de venta detallada en PDF
  const generateTicket = async (saleData) => {
    try {
      console.log('Iniciando generación de ticket PDF para venta detallada...');
      setIsGeneratingTicket(true);
      let formattedDate = '';
      let formattedTime = '';
      let businessTimezone = 'America/Mexico_City';
      
      // Obtener el business_id del punto de venta asignado
      let businessId = null;
      if (assignedPointOfSale && assignedPointOfSale.business_id) {
        businessId = assignedPointOfSale.business_id;
        console.log('Business ID para obtener hora:', businessId);
      }
      
      // Intentar obtener la hora del servidor
      try {
        console.log('Intentando obtener la hora del servidor...');
        const serverTimeResponse = await axios.get('/api/seller/time', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          timeout: 5000
        });
        
        console.log('Respuesta de get_business_time:', serverTimeResponse.data);
        
        const serverTimeStr = serverTimeResponse.data.time;
        businessTimezone = serverTimeResponse.data.timezone || 'America/Mexico_City';
        
        try {
          console.log('Fecha y hora recibida del servidor:', serverTimeStr);
          
          if (!serverTimeStr || typeof serverTimeStr !== 'string') {
            throw new Error('Formato de fecha inválido recibido del servidor');
          }
          
          // Extraer directamente las partes de la fecha y hora
          const dateParts = serverTimeStr.split('T');
          
          if (dateParts.length !== 2) {
            throw new Error('Formato de fecha/hora inesperado');
          }
          
          // Extraer la fecha (YYYY-MM-DD)
          const rawDate = dateParts[0];
          const dateComponents = rawDate.split('-');
          if (dateComponents.length !== 3) {
            throw new Error('Formato de fecha inesperado');
          }
          
          // Formatear la fecha como DD/MM/YYYY
          formattedDate = `${dateComponents[2]}/${dateComponents[1]}/${dateComponents[0]}`;
          
          // Extraer la hora (HH:MM:SS)
          const rawTime = dateParts[1].split('.')[0]; // Eliminar milisegundos y zona horaria
          formattedTime = rawTime;
          
          console.log('Fecha extraída:', formattedDate);
          console.log('Hora extraída:', formattedTime);
          console.log('Zona horaria del negocio:', businessTimezone);
          
        } catch (formatError) {
          console.error('Error al extraer la fecha y hora:', formatError);
          throw new Error('Error al procesar la fecha del servidor');
        }
      } catch (serverTimeError) {
        console.error('Error al obtener la hora del servidor:', serverTimeError);
        console.log('Usando fecha y hora local como alternativa...');
        
        // Usar la fecha y hora local como respaldo
        const now = new Date();
        formattedDate = now.toLocaleDateString('es-MX', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        formattedTime = now.toLocaleTimeString('es-MX', { 
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false 
        });
        
        console.log('Fecha local (respaldo):', formattedDate);
        console.log('Hora local (respaldo):', formattedTime);
      }
      
      // Obtener datos del negocio asociado al punto de venta asignado
      let business = {};
      let user = {};
      
      // Verificar si tenemos el punto de venta asignado y su business_id
      if (!assignedPointOfSale || !assignedPointOfSale.business_id) {
        console.error('No se encontró el punto de venta asignado o su business_id');
        try {
          const posResponse = await axios.get('/api/points-of-sale/assigned', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            timeout: 5000
          });
          
          if (posResponse.data && posResponse.data.pointsOfSale && posResponse.data.pointsOfSale.length > 0) {
            const pos = posResponse.data.pointsOfSale[0];
            console.log('Punto de venta obtenido:', pos);
            if (pos.business_id) {
              try {
                console.log('Intentando obtener datos del negocio con ID:', pos.business_id);
                const businessDetailResponse = await axios.get(`/api/businesses/${pos.business_id}`, {
                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                  timeout: 5000
                });
                
                if (businessDetailResponse.data) {
                  business = businessDetailResponse.data;
                  console.log('Datos del negocio obtenidos:', business);
                }
              } catch (businessError) {
                console.error('Error al obtener datos del negocio:', businessError);
              }
            }
          }
        } catch (posError) {
          console.error('Error al obtener el punto de venta asignado:', posError);
        }
      } else {
        // Tenemos el punto de venta asignado y su business_id, obtener los datos del negocio directamente
        try {
          console.log('Obteniendo datos del negocio con business_id:', assignedPointOfSale.business_id);
          const businessDetailResponse = await axios.get(`/api/businesses/${assignedPointOfSale.business_id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            timeout: 5000
          });
          
          if (businessDetailResponse.data) {
            business = businessDetailResponse.data;
            console.log('Datos del negocio obtenidos:', business);
          } else {
            console.error('No se obtuvieron datos del negocio');
          }
        } catch (businessError) {
          console.error('Error al obtener datos del negocio:', businessError);
          
          // Intentar obtener los datos del negocio a través de /api/businesses/me como respaldo
          try {
            console.log('Intentando obtener datos del negocio como respaldo');
            const businessResponse = await axios.get('/api/businesses/me', {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
              timeout: 5000
            });
            business = businessResponse.data;
            console.log('Datos del negocio obtenidos como respaldo:', business);
          } catch (backupError) {
            console.error('Error al obtener datos del negocio como respaldo:', backupError);
            // Continuar con valores predeterminados
            business = { bis_name: 'Negocio', bis_phone: 'No disponible' };
          }
        }
      }
      
      // Si después de todos los intentos no tenemos los datos del negocio, usar valores predeterminados
      if (!business || !business.bis_name || !business.bis_phone) {
        console.warn('No se obtuvieron datos completos del negocio, usando valores predeterminados');
        business = { 
          ...business,
          bis_name: business.bis_name || 'Negocio', 
          bis_phone: business.bis_phone || 'No disponible' 
        };
      }
      
      // Obtener datos del usuario
      try {
        const userResponse = await axios.get('/api/users/me', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          timeout: 3000
        });
        user = userResponse.data;
        console.log('Datos del usuario obtenidos:', user);
      } catch (userError) {
        console.error('Error al obtener datos del usuario:', userError);
        // Continuar con valores predeterminados
        user = { full_name: 'Usuario' };
      }
      
      // Crear el documento PDF con ancho de 80mm (ticket)
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [80, 300] // Ancho de 80mm, altura ajustable
      });
      
      doc.setFont('helvetica');
      doc.setFontSize(10);
      
      // Encabezado del ticket
      doc.setFontSize(10);
      doc.text(business.bis_name || 'Negocio', 40, 10, null, null, 'center');
      doc.text('Ticket de Venta', 40, 15, null, null, 'center');
      doc.text(`Tel: ${business.bis_phone || 'No disponible'}`, 40, 20, null, null, 'center');
      doc.text('Venta Detallada', 40, 25, null, null, 'center');
      
      // Información de la venta
      const displayPosName = assignedPointOfSale ? assignedPointOfSale.pos_name : 'No disponible';
      
      doc.text(`Punto de venta: ${displayPosName}`, 2, 35);
      // Truncar el nombre del usuario a 20 caracteres
      const userName = user && user.full_name ? user.full_name.substring(0, 20) : 'No disponible';
      doc.text(`Vendedor: ${userName}`, 2, 40);
      doc.text(`Fecha: ${formattedDate || 'No disponible'}`, 2, 45);
      doc.text(`Hora: ${formattedTime || 'No disponible'} (${businessTimezone || 'UTC'})`, 2, 50);
      
      // Línea divisoria
      doc.line(2, 55, 78, 55);
      doc.setFontSize(9);
      doc.text('Detalle de la venta', 40, 60, null, null, 'center');
      doc.setFontSize(8);
      doc.text('Producto', 2, 65);
      doc.text('Cant.', 45, 65, null, null, 'center');
      doc.text('Subtotal', 75, 65, null, null, 'right');
      
      // Línea divisoria
      doc.line(2, 70, 78, 70);
      
      // Detalles de los productos
      let y = 75;
      saleData.products.forEach(product => {
        // Obtener el nombre del producto, asegurando que existe
        const productName = product.name || product.prod_name || 'Producto';
        
        // Truncar el nombre si es necesario
        const displayName = productName.length > 25 ? productName.substring(0, 22) + '...' : productName;
        
        // Calcular el subtotal si no está definido
        const subtotal = product.subtotal || (product.price * product.quantity) - (product.discount || 0);
        
        doc.text(displayName, 2, y);
        doc.text(product.quantity.toString(), 45, y, null, null, 'center');
        doc.text(`$${subtotal.toFixed(2)}`, 75, y, null, null, 'right');
        y += 5;
      });
      
      // Línea divisoria
      doc.line(2, y, 78, y);
      y += 5;
      
      // Totales
      const subtotalValue = typeof saleData.subtotal === 'number' ? saleData.subtotal : 0;
      doc.text(`Subtotal: $${subtotalValue.toFixed(2)}`, 75, y, null, null, 'right');
      y += 5;
      
      // Mostrar descuento general siempre, incluso cuando sea 0
      // Asegurar que el valor del descuento sea un número
      let generalDiscountValue = 0;
      if (typeof saleData.generalDiscount === 'number') {
        generalDiscountValue = saleData.generalDiscount;
      } else if (typeof saleData.generalDiscount === 'string' && saleData.generalDiscount !== '' && saleData.generalDiscount !== '.') {
        generalDiscountValue = parseFloat(saleData.generalDiscount);
      } else if (typeof saleData.general_discount === 'number') {
        generalDiscountValue = saleData.general_discount;
      } else if (typeof saleData.general_discount === 'string' && saleData.general_discount !== '' && saleData.general_discount !== '.') {
        generalDiscountValue = parseFloat(saleData.general_discount);
      }
      
      // Asegurar que no sea NaN
      if (isNaN(generalDiscountValue)) generalDiscountValue = 0;
      
      doc.text(`Descuento: $${generalDiscountValue.toFixed(2)}`, 75, y, null, null, 'right');
      y += 5;
      
      // Total antes de impuestos
      const totalBeforeTaxValue = typeof saleData.totalBeforeTax === 'number' ? saleData.totalBeforeTax : 
                                (typeof saleData.total_before_tax === 'number' ? saleData.total_before_tax : subtotalValue - generalDiscountValue);
      doc.text(`Total antes de impuesto: $${totalBeforeTaxValue.toFixed(2)}`, 75, y, null, null, 'right');
      y += 5;
      
      // Impuesto si es mayor a 0
      const taxAmountValue = typeof saleData.taxAmount === 'number' ? saleData.taxAmount : 
                          (typeof saleData.tax_amount === 'number' ? saleData.tax_amount : 0);
      const taxRateValue = typeof saleData.taxRate === 'number' ? saleData.taxRate : 
                         (typeof saleData.tax_rate === 'number' ? saleData.tax_rate : 0);
      
      if (taxAmountValue > 0) {
        doc.text(`Impuesto (${taxRateValue.toFixed(2)}%): $${taxAmountValue.toFixed(2)}`, 75, y, null, null, 'right');
        y += 5;
      }
      
      // Gran total
      doc.setFontSize(10);
      // Asegurar que grandTotal existe y es un número
      const grandTotalValue = typeof saleData.grandTotal === 'number' ? saleData.grandTotal : 
                             (typeof saleData.grand_total === 'number' ? saleData.grand_total : 0);
      doc.text(`Gran Total: $${grandTotalValue.toFixed(2)}`, 75, y, null, null, 'right');
      doc.setFontSize(8);
      y += 10;
      
      // Método de pago
      const paymentMethod = saleData.paymentMethod || saleData.payment_method || 'Efectivo';
      doc.text(`Método de pago: ${paymentMethod}`, 2, y);
      y += 10;
      
      // Pie de página
      doc.text('Gracias por su compra', 40, y, null, null, 'center');
      y += 5;
      
      // Línea en blanco
      y += 5;
      
      // ID de venta
      doc.text('IdVenta', 40, y, null, null, 'center');
      y += 5;
      
      // Generar string de ID venta: fecha+hora+nombre del punto de venta+nombre del vendedor (truncado a 12 caracteres)+monto de venta (sin decimales)
      const dateForId = formattedDate.replace(/\//g, '');
      const timeForId = formattedTime.replace(/:/g, '').replace(/\s/g, '');
      const posName = (assignedPointOfSale?.pos_name || 'POS').replace(/\s+/g, '');
      const sellerName = (user?.full_name || 'Vendedor').substring(0, 12).replace(/\s+/g, '');
      const amountForId = Math.floor(grandTotalValue).toString();
      
      const ventaId = `${dateForId}${timeForId}${posName}${sellerName}${amountForId}`;
      doc.text(ventaId, 40, y, null, null, 'center');
      y += 5;
      
      // Generar el nombre del archivo con la fecha y hora
      const dateForFilename = formattedDate.replace(/\//g, '-');
      const timeForFilename = formattedTime.replace(/:/g, '-').replace(/\s/g, '');
      const filename = `ticket_venta_detallada_${dateForFilename}_${timeForFilename}.pdf`;
      
      console.log('Intentando descargar el PDF...');
      
      // Método 1: Método directo de jsPDF (más confiable)
      try {
        console.log('Usando método doc.save() directo');
        doc.save(filename);
        console.log('PDF guardado con método directo');
        setIsGeneratingTicket(false);
        return true;
      } catch (saveError) {
        console.error('Error con método directo:', saveError);
        
        // Si falla el método directo, intentar con el método de Blob
        try {
          const blob = doc.output('blob');
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.style.display = 'none';
          document.body.appendChild(link);
          
          console.log('Descargando con Blob y URL.createObjectURL');
          link.click();
          
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            console.log('Limpieza de recursos completada');
          }, 1000);
          
          console.log('PDF descargado con método Blob');
          setIsGeneratingTicket(false);
          return true;
        } catch (blobError) {
          console.error('Error con método Blob:', blobError);
          
          // Método 3: Último recurso, usar output base64 y abrir en nueva ventana
          try {
            const base64 = doc.output('datauristring');
            window.open(base64);
            console.log('PDF abierto en nueva ventana con base64');
            setIsGeneratingTicket(false);
            return true;
          } catch (base64Error) {
            console.error('Error con método base64:', base64Error);
          }
        }
      }
      
      setIsGeneratingTicket(false);
      return false;
    } catch (error) {
      console.error('Error al generar el ticket:', error);
      setIsGeneratingTicket(false);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
        return;
      }
      
      // Obtener fecha/hora del negocio actualizada antes de enviar la venta
      let currentBusinessTime = businessDateTime;
      if (assignedPointOfSale?.business_id) {
        console.log('Obteniendo fecha/hora actualizada del negocio para la venta detallada...');
        const freshBusinessTime = await fetchBusinessTime(assignedPointOfSale.business_id);
        if (freshBusinessTime) {
          currentBusinessTime = freshBusinessTime;
          console.log('Fecha/hora actualizada del negocio obtenida:', currentBusinessTime);
        } else {
          console.warn('No se pudo obtener la fecha/hora actualizada del negocio, usando valor anterior o fecha local');
        }
      }

      // Validaciones
      if (!formData.sale_date || !formData.payment_method) {
        setError('Todos los campos marcados con * son obligatorios');
        return;
      }

      if (selectedProducts.length === 0) {
        setError('Debe agregar al menos un producto a la venta');
        return;
      }

      if (!assignedPointOfSale) {
        setError('No se ha encontrado un punto de venta asignado');
        return;
      }

      // Preparar detalles de productos con nombre para el ticket
      const productDetails = selectedProducts.map(product => ({
        product_id: product.id,
        quantity: product.quantity,
        price: product.prod_price,
        discount: product.discount || 0,
        subtotal: (product.prod_price * product.quantity) - (product.discount || 0),
        name: product.prod_name // Añadir el nombre del producto para el ticket
      }));
      
      // Calcular el total antes de impuestos y el monto del impuesto para el ticket
      const totalBeforeTaxValue = subtotal - generalDiscount;
      const taxAmountValue = (totalBeforeTaxValue * taxRate) / 100;

      // Combinar la fecha seleccionada por el usuario con la zona horaria del negocio
      let finalSaleDate = formData.sale_date;
      
      try {
        // Si tenemos la fecha/hora del negocio y el usuario ha seleccionado una fecha
        if (currentBusinessTime && formData.sale_date) {
          console.log('Fecha seleccionada por el usuario (original):', formData.sale_date);
          
          // Extraer componentes de la fecha seleccionada por el usuario
          // Formato esperado: YYYY-MM-DD
          const [userYear, userMonth, userDay] = formData.sale_date.split('-').map(Number);
          
          // Crear objeto Date con la fecha/hora del negocio
          const businessDate = new Date(currentBusinessTime);
          console.log('Fecha/hora del negocio (original):', businessDate.toISOString());
          
          // Crear una nueva fecha combinando la fecha del usuario con la hora del negocio
          // IMPORTANTE: Usamos UTC para evitar problemas de zona horaria
          const combinedDate = new Date(Date.UTC(
            userYear,
            userMonth - 1, // Meses en JavaScript son 0-indexed
            userDay,
            businessDate.getUTCHours(),
            businessDate.getUTCMinutes(),
            businessDate.getUTCSeconds(),
            businessDate.getUTCMilliseconds()
          ));
          
          // Convertir a string ISO
          finalSaleDate = combinedDate.toISOString();
          console.log('Fecha combinada (UTC, fecha del usuario + hora del negocio):', finalSaleDate);
        } else {
          // Si no tenemos fecha/hora del negocio, usar la fecha seleccionada por el usuario
          console.log('Usando fecha seleccionada por el usuario:', formData.sale_date);
        }
      } catch (dateError) {
        console.error('Error al combinar fecha del usuario con zona horaria del negocio:', dateError);
        // En caso de error, usar la fecha seleccionada por el usuario como respaldo
      }
      
      // Datos de la venta
      const saleData = {
        point_of_sale_id: assignedPointOfSale.id,
        sale_date: finalSaleDate,
        total_amount: grandTotal, // Añadir el campo total_amount requerido por el controlador
        payment_method: formData.payment_method,
        customer_id: formData.customer_id || (customers.length > 0 ? customers[0].id : null), // Usar el primer cliente como fallback
        notes: formData.notes,
        products: productDetails,
        general_discount: generalDiscount,
        tax_rate: taxRate,
        subtotal: subtotal,
        grand_total: grandTotal,
        // Datos adicionales para el ticket
        totalBeforeTax: totalBeforeTaxValue,
        taxAmount: taxAmountValue,
        generalDiscount: generalDiscount,
        paymentMethod: formData.payment_method
      };
      
      // Log para depuración
      console.log('Registrando venta detallada con fecha/hora:', currentBusinessTime || formData.sale_date);

      // Enviar datos y capturar la respuesta
      console.log('Enviando datos de venta detallada al servidor:', JSON.stringify(saleData));
      const response = await axios.post('/api/sales/detailed', saleData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Verificar la respuesta del servidor
      console.log('Respuesta del servidor:', response.data);
      
      if (response.data && response.data.success) {
        console.log('Venta registrada exitosamente con ID:', response.data.data?.id);
        
        // Generar ticket si está marcada la opción
        if (printTicket) {
          console.log('Generando ticket de venta detallada...');
          setIsGeneratingTicket(true); // Indicador visual de que se está generando el ticket
          setSuccessMessage('Venta registrada. Generando ticket...');
          
          try {
            // Asegurar que el saleData tenga toda la información necesaria para el ticket
            const ticketData = {
              ...saleData,
              // Añadir el ID de la venta si está disponible
              sale_id: response.data.data?.id || 'N/A',
              // Asegurar que tenemos los datos de los productos con nombres
              products: selectedProducts.map(product => ({
                product_id: product.id,
                quantity: product.quantity,
                price: product.prod_price,
                discount: product.discount || 0,
                subtotal: (product.prod_price * product.quantity) - (product.discount || 0),
                name: product.prod_name
              })),
              // Asegurar que tenemos los valores de totales correctamente
              grandTotal: grandTotal,
              subtotal: subtotal,
              totalBeforeTax: totalBeforeTaxValue,
              taxAmount: taxAmountValue,
              taxRate: taxRate,
              // Asegurar que el descuento sea un número válido para el ticket
              generalDiscount: typeof generalDiscount === 'string' ? 
                (generalDiscount === '' || generalDiscount === '.' ? 0 : parseFloat(generalDiscount)) : 
                (typeof generalDiscount === 'number' ? generalDiscount : 0),
              general_discount: typeof generalDiscount === 'string' ? 
                (generalDiscount === '' || generalDiscount === '.' ? 0 : parseFloat(generalDiscount)) : 
                (typeof generalDiscount === 'number' ? generalDiscount : 0)
            };
            
            // Generar el ticket con los datos completos
            const ticketResult = await generateTicket(ticketData);
            
            if (ticketResult) {
              console.log('Ticket generado correctamente');
              setSuccessMessage('Venta registrada exitosamente. Ticket generado.');
            } else {
              console.error('No se pudo generar el ticket');
              setSuccessMessage('Venta registrada exitosamente. No se pudo generar el ticket.');
            }
          } catch (ticketError) {
            console.error('Error al generar el ticket:', ticketError);
            setSuccessMessage('Venta registrada exitosamente. Error al generar el ticket.');
          } finally {
            setIsGeneratingTicket(false);
          }
        } else {
          setSuccessMessage('Venta registrada exitosamente');
        }
        
        // Limpiar formulario
        setFormData({
          sale_date: new Date().toISOString().split('T')[0],
          payment_method: 'Efectivo',
          customer_id: formData.customer_id, // Mantener el cliente seleccionado
          notes: ''
        });
        setSelectedProducts([]);
        setGeneralDiscount(0.00);
        setTaxRate(0.00);
        setPrintTicket(false); // Resetear la opción de imprimir ticket

        // Mostrar mensaje de éxito por 3 segundos
        setTimeout(() => {
          if (isMounted.current) {
            setSuccessMessage(null);
          }
        }, 3000);
      } else {
        // Si la respuesta no indica éxito, mostrar un error
        console.error('La respuesta del servidor no indica éxito:', response.data);
        setError('La venta no se pudo registrar correctamente. Por favor, inténtelo de nuevo.');
      }
    } catch (error) {
      console.error('Error al registrar la venta:', error);
      setError(error.response?.data?.message || 'Error al registrar la venta. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Mostrar pantalla de carga
  if (initialLoading) {
    return (
      <div className="detailed-sale-form-container">
        <h3>Venta Detallada</h3>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Cargando datos...</p>
        </div>
      </div>
    );
  }

  // Mostrar pantalla de error si hay un error
  if (error) {
    return (
      <div className="detailed-sale-form-container">
        <h3>Venta Detallada</h3>
        <div className="error-message">{error}</div>
        <div className="form-actions">
          <button type="button" className="cancel-btn" onClick={onCancel}>
            Volver
          </button>
        </div>
      </div>
    );
  }
  
  // Mostrar pantalla de error si no hay punto de venta asignado
  if (!assignedPointOfSale) {
    return (
      <div className="detailed-sale-form-container">
        <h3>Venta Detallada</h3>
        <div className="error-message">No se ha podido cargar el punto de venta asignado.</div>
        <div className="form-actions">
          <button type="button" className="cancel-btn" onClick={onCancel}>
            Volver
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="detailed-sale-form-container">
      <h3>Venta Detallada</h3>
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}
      
      <form className="detailed-sale-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="point_of_sale_id">Punto de Venta *</label>
          {assignedPointOfSale ? (
            <div className="assigned-pos-info">
              <div className="pos-display">
                <strong>{assignedPointOfSale.pos_name}</strong>
                <span className="pos-badge">Asignado</span>
              </div>
              <input type="hidden" id="point_of_sale_id" name="point_of_sale_id" value={assignedPointOfSale.id} />
            </div>
          ) : (
            <div className="error-message">No tienes un punto de venta asignado. Por favor, contacta al administrador.</div>
          )}
        </div>
        
        <div className="form-group">
          <label htmlFor="customer_id">Cliente</label>
          <select
            id="customer_id"
            name="customer_id"
            value={formData.customer_id}
            onChange={handleChange}
          >
            <option value="">Seleccione un cliente</option>
            {customers.map(customer => (
              <option key={customer.id} value={customer.id}>
                {customer.cus_name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label htmlFor="sale_date">Fecha de Venta *</label>
          <input
            type="date"
            id="sale_date"
            name="sale_date"
            value={formData.sale_date}
            onChange={handleChange}
            required
          />
        </div>
        
        {/* Sección de productos */}
        <div className="products-section">
          <h4>Productos</h4>
          
          <div className="add-product-form">
            <div className="product-search">
              <input
                type="text"
                placeholder="Buscar por código o nombre del producto..."
                value={productSearchTerm}
                onChange={handleProductSearchChange}
                className="product-search-input"
              />
            </div>
            <div className="product-selection">
              <select
                id="product_id"
                value={selectedProductId}
                onChange={handleProductChange}
                disabled={filteredProducts.length === 0}
              >
                <option value="">Seleccione un producto</option>
                {filteredProducts.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.prod_code ? `[${product.prod_code}] ` : ''}{product.prod_name} - ${product.prod_price.toFixed(2)}
                  </option>
                ))}
              </select>
              
              <div className="quantity-input">
                <label htmlFor="quantity">Cantidad:</label>
                <input
                  type="text"
                  id="quantity"
                  value={productQuantity}
                  onChange={handleQuantityChange}
                  placeholder="0.00"
                  disabled={!selectedProductId}
                  className="numeric-input"
                />
              </div>
              
              <div className="discount-input">
                <label htmlFor="discount">Descuento total ($):</label>
                <input
                  type="text"
                  id="discount"
                  value={productDiscount}
                  onChange={handleDiscountChange}
                  disabled={!selectedProductId}
                  className="numeric-input-no-arrows"
                  placeholder="0.00"
                />
              </div>
              
              <button
                type="button"
                className="add-product-btn"
                onClick={handleAddProduct}
                disabled={!selectedProductId}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="10" y1="11" x2="14" y2="11"></line>
                </svg>
                Agregar
              </button>
            </div>
          </div>
          
          {selectedProducts.length > 0 ? (
            <div className="selected-products">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Precio</th>
                    <th>Cantidad</th>
                    <th>Descuento</th>
                    <th>Subtotal</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedProducts.map(product => {
                    // Asegurar que discount sea un número para el cálculo
                    const discountValue = typeof product.discount === 'string' ?
                      (product.discount === '' || product.discount === '.' ? 0 : parseFloat(product.discount)) :
                      (product.discount || 0);
                    const subtotal = (product.prod_price * product.quantity) - discountValue;
                    return (
                      <tr key={product.id}>
                        <td>{product.prod_name}</td>
                        <td>${product.prod_price.toFixed(2)}</td>
                        <td>
                          <div className="quantity-control">
                            <button
                              type="button"
                              className="quantity-btn"
                              onClick={() => handleUpdateQuantity(product.id, Math.max(0.01, (parseFloat(product.quantity) - 0.5)).toFixed(2))}
                              disabled={parseFloat(product.quantity) <= 0.5}
                            >
                              -
                            </button>
                            <span>{parseFloat(product.quantity).toFixed(2)}</span>
                            <button
                              type="button"
                              className="quantity-btn"
                              onClick={() => handleUpdateQuantity(product.id, (parseFloat(product.quantity) + 0.5).toFixed(2))}
                            >
                              +
                            </button>
                          </div>
                        </td>
                        <td>
                          <div className="discount-control">
                            <input
                              type="text"
                              className="discount-input-field numeric-input-no-arrows"
                              value={product.discount}
                              onChange={(e) => {
                                // Obtener el valor del input
                                let value = e.target.value;
                                
                                // Validar que solo contenga números y un punto decimal
                                if (/^\d*\.?\d*$/.test(value) || value === '') {
                                  handleUpdateDiscount(product.id, value);
                                }
                              }}
                              placeholder="0.00"
                            />
                          </div>
                        </td>
                        <td>${subtotal.toFixed(2)}</td>
                        <td>
                          <button
                            type="button"
                            className="remove-product-btn"
                            onClick={() => handleRemoveProduct(product.id)}
                          >
                            Eliminar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="4" className="total-label">Subtotal:</td>
                    <td colSpan="2" className="total-amount">${subtotal.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="no-products-message">
              No hay productos agregados a la venta.
            </div>
          )}
        </div>
        
        {/* Sección de totales */}
        {selectedProducts.length > 0 && (
          <div className="totals-section">
            <div className="totals-grid">
              <div className="total-row">
                <div className="total-label">Descuento General ($):</div>
                <div className="total-input">
                  <input
                    type="text"
                    value={generalDiscount}
                    onChange={handleGeneralDiscountChange}
                    className="numeric-input-no-arrows"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="total-row">
                <div className="total-label">Total antes de Impuesto:</div>
                <div className="total-value">${totalBeforeTax.toFixed(2)}</div>
              </div>
              <div className="total-row">
                <div className="total-label">Impuesto (%):</div>
                <div className="total-input">
                  <input
                    type="number"
                    value={taxRate.toFixed(2)}
                    onChange={handleTaxRateChange}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div className="total-row grand-total">
                <div className="total-label">Gran Total:</div>
                <div className="total-value">${grandTotal.toFixed(2)}</div>
              </div>
            </div>
          </div>
        )}
        
        <div className="form-group">
          <label htmlFor="payment_method">Método de Pago *</label>
          <div className="payment-method-options">
            <div className={`payment-option ${formData.payment_method === 'Efectivo' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="payment_cash"
                name="payment_method"
                value="Efectivo"
                checked={formData.payment_method === 'Efectivo'}
                onChange={handleChange}
                required
              />
              <label htmlFor="payment_cash">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="6" width="20" height="12" rx="2"></rect>
                  <circle cx="12" cy="12" r="2"></circle>
                  <path d="M6 12h.01M18 12h.01"></path>
                </svg>
                Efectivo
              </label>
            </div>
            
            <div className={`payment-option ${formData.payment_method === 'Tarjeta' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="payment_card"
                name="payment_method"
                value="Tarjeta"
                checked={formData.payment_method === 'Tarjeta'}
                onChange={handleChange}
              />
              <label htmlFor="payment_card">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                  <line x1="1" y1="10" x2="23" y2="10"></line>
                </svg>
                Tarjeta
              </label>
            </div>
            
            <div className={`payment-option ${formData.payment_method === 'Transferencia' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="payment_transfer"
                name="payment_method"
                value="Transferencia"
                checked={formData.payment_method === 'Transferencia'}
                onChange={handleChange}
              />
              <label htmlFor="payment_transfer">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"></polyline>
                  <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"></path>
                </svg>
                Transferencia
              </label>
            </div>
          </div>
          
          {/* Checkbox para generar ticket */}
          <div className="form-group print-ticket-option">
            <label className="checkbox-container">
              <input
                type="checkbox"
                name="printTicket"
                id="printTicket"
                checked={printTicket}
                onChange={handleChange}
              />
              <span className="checkbox-label">Generar Ticket de Venta (80mm)</span>
            </label>
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="notes">Notas</label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows="3"
            placeholder="Notas adicionales sobre la venta..."
          ></textarea>
        </div>
        
        <div className="form-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="submit-btn"
            disabled={loading || !assignedPointOfSale}
          >
            {loading ? 'Registrando...' : 'Registrar Venta'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SellerDetailedSaleForm;
